const ACTIVITY_KEY = 'sematics:last-activity'
const CHANNEL = 'sematics-session'
const LAST_LOGIN_INFO_KEY = 'lastLoginInfo'

let authenticated = undefined
let channel = null
const sessionLostHandlers = new Set()

const getChannel = () => {
  if (typeof BroadcastChannel === 'undefined') return null
  if (!channel) channel = new BroadcastChannel(CHANNEL)
  return channel
}

const postAuth = async (path, body) => {
  const response = await fetch(`/auth/${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body || {}),
  })
  let data = null
  try {
    data = await response.json()
  } catch (err) {
    data = null
  }
  if (!data) {
    return { success: false, message: `Request failed (${response.status})` }
  }
  return data
}

export const fetchSessionStatus = async () => {
  try {
    const response = await fetch('/api/session', { credentials: 'same-origin' })
    const data = await response.json()
    authenticated = data?.authenticated === true
    return data
  } catch (err) {
    authenticated = false
    return { authenticated: false, expiresAt: null }
  }
}

export const isUserAuthenticated = () => authenticated === true

export const setAuthenticated = (value) => {
  authenticated = value === true
}

export const login = (payload) => postAuth('login', payload)
export const logout = () => postAuth('logout', {})
export const switchCompany = (companyId) => postAuth('switch-company', { companyId })
export const updateUnifyPreference = (payload) => postAuth('preference', payload)

export const fetchWsTicket = async () => {
  const response = await fetch('/api/ws-ticket', { credentials: 'same-origin' })
  if (!response.ok) throw new Error(`ws ticket ${response.status}`)
  const data = await response.json()
  return data.ticket
}

export const getLastLoginInfo = () => localStorage.getItem(LAST_LOGIN_INFO_KEY)

export const storeLastLoginInfo = (value) => {
  if (value && value.length > 0) localStorage.setItem(LAST_LOGIN_INFO_KEY, value)
}

export const markActivity = (at = Date.now()) => {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(at))
  } catch (err) {
    // storage unavailable; server-side bounds still apply
  }
}

export const clearActivity = () => {
  try {
    localStorage.removeItem(ACTIVITY_KEY)
  } catch (err) {
    // nothing to clear
  }
}

export const broadcastSessionSignal = (signal) => {
  try {
    getChannel()?.postMessage(signal)
  } catch (err) {
    // best effort
  }
}

export const onSessionSignal = (handler) => {
  const ch = getChannel()
  if (!ch) return () => {}
  const listener = (event) => {
    if (event.data === 'logged-out' || event.data === 'logged-in') handler(event.data)
  }
  ch.addEventListener('message', listener)
  return () => ch.removeEventListener('message', listener)
}

export const onSessionLost = (handler) => {
  sessionLostHandlers.add(handler)
  return () => sessionLostHandlers.delete(handler)
}

export const emitSessionLost = () => {
  authenticated = false
  clearActivity()
  sessionLostHandlers.forEach((handler) => {
    try {
      handler()
    } catch (err) {
      console.error(err)
    }
  })
}

export const getLegacyReportToken = () => {
  if (!/\/reports-view/.test(window.location.pathname)) return null
  return new URLSearchParams(window.location.search).get('auth_token')
}

export const PRESERVED_STORAGE_KEYS = [ACTIVITY_KEY, LAST_LOGIN_INFO_KEY]

export const IDLE_TIMEOUT_MS = 30 * 60 * 1000

export const readLastActivity = () => {
  try {
    const value = Number(localStorage.getItem(ACTIVITY_KEY))
    return Number.isFinite(value) && value > 0 ? value : null
  } catch (err) {
    return null
  }
}

export const idleFor = (now = Date.now()) => {
  const last = readLastActivity()
  return last === null ? null : now - last
}

export const extendSession = async () => {
  const response = await fetch('/api/session', { method: 'POST', credentials: 'same-origin' })
  const data = await response.json().catch(() => null)
  if (data?.authenticated) markActivity()
  return data
}
